import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, theme, Button, message, Input } from "antd";
import { GoldOutlined, PlusOutlined } from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";

import AdminTopBar from "../../components/TopBar/AdminTopbar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import { GroupTable } from "../../components/Table";
import { AddGroupModal } from "../../components/ModalForm";
import { EditGroupModal } from "../../components/ModalForm";

import { AuthContext } from "../../context/AuthContext";

import "../../styles/Groups.css";
import { getGroupsApi, addGroupApi } from "../../services/Admin/AdminApis";
import {
  editGroupApi,
  removeGroupApi,
} from "../../services/Group/GroupApis";

const { Content } = Layout;
const { Search } = Input;

const AdminGroupManagementPage: React.FC = () => {
  const [groups, setGroups] = useState<any>([]);
  const [group, setGroup] = useState<any>({});
  const [filtered, setFiltered] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>(
    () => sessionStorage.getItem("adminGroupsSearch") || ""
  );
  const [show, setShow] = useState(false);
  const [update, setUpdate] = useState(false);
  const [isActive, setActive] = useState(true);

  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const showModal = () => {
    setShow(!show);
    setUpdate(false);
  };

  const showEditModal = (data: any) => {
    setGroup(data);
    setShow(!show);
    setUpdate(true);
  };

  const handleManage = (id: string) => {
    navigate(`/admin/groups/${id}`);
  };

  const handleAdd = async (data: any) => {
    const { GroupType, ...rest } = data;
    const payload = {
      ...rest,
      IsExternal: GroupType === "External",
    };

    return await addGroupApi(payload, currentUser.id)
      .then((res) => {
        setGroups([...groups, res.data]);
        return res;
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleUpdate = async (id: string, data: any) => {
    return await editGroupApi(id, data)
      .then((res) => {
        const uppdatedUsers = groups.map((user: any) => {
          if (id === user.id) {
            return { ...user, ...res.data };
          }
          return user;
        });
        setGroups(uppdatedUsers);
        return res;
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleDelete = (id: string) => {
    removeGroupApi(id)
      .then((res) => {
        const deleted = res.data;
        setGroups(groups.filter((group: any) => group.id !== deleted.id));
        return res;
      })
      .catch((err) => {
        message.error(err);
        console.log(err);
      });
  };

  const onSearch = (e: any) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) sessionStorage.setItem("adminGroupsSearch", value);
    else sessionStorage.removeItem("adminGroupsSearch");
    if (value !== "") {
      const filteredData = groups.filter((row: any) => {
        return Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(value.toLowerCase());
      });
      setFiltered(filteredData);
    }
  };

  useEffect(() => {
    setActive(true);
    getGroupsApi()
      .then((res) => {
        setGroups(res.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setActive(false);
      });
  }, []);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <AdminSideBar />
        <Layout className="site-layout">
          <AdminTopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <GoldOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Groups
              </h2>
              <div className="interactions">
                <Button onClick={showModal} size="large">
                  <PlusOutlined style={{ paddingRight: "5px" }} />
                  Add Group
                </Button>

                <div>
                  <Search
                    placeholder="Search"
                    size="large"
                    value={searchValue}
                    onChange={(e) => onSearch(e)}
                    style={{ width: "100%", margin: "24px 0" }}
                  />
                </div>

                {update ? (
                  <EditGroupModal
                    show={show}
                    group={group}
                    showModal={showModal}
                    handleUpdate={handleUpdate}
                  />
                ) : (
                  <AddGroupModal
                    show={show}
                    showModal={showModal}
                    handleAdd={handleAdd}
                  />
                )}
              </div>

              <GroupTable
                groups={groups}
                filtered={filtered}
                showEditModal={showEditModal}
                handleManage={handleManage}
                handleDelete={handleDelete}
                storagePrefix="admin-groups"
              />
            </div>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default AdminGroupManagementPage;
